import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { uploadImage } from "../../common/uploadImage";
import {
  Save,
  Upload,
  Image,
  X,
  ChevronLeft,
  Loader2,
  Package,
  CheckCircle,
  XCircle,
  Type,
  FileText,
  List,
  Info,
  Grid,
  Settings,
  Trash2,
  Eye,
  Plus,
  AlertCircle,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],

    [{ color: [] }, { background: [] }],

    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState("basic");
  const [showPreview, setShowPreview] = useState(false);

  const [form, setForm] = useState({
    heading: "",
    slug: "",
    description: "",
    mainImage: "",
    smallImages: [],
    bulletDescription: [],
    additionalPoints: [],
    moreProductInfo: [],
    category: "",
    isActive: true,
  });

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetching(true);
        const [productRes, categoryRes] = await Promise.all([
          axios.get(`${API}/product/${id}`),
          axios.get(`${API}/categories`),
        ]);

        const product = productRes.data.product;
        console.log("Product data:", product);

        setForm({
          heading: product.heading || "",
          slug: product.slug || "",
          description: product.description || "",
          mainImage: product.mainImage || "",
          smallImages: product.smallImages || [],
          bulletDescription: product.bulletDescription || [],
          additionalPoints: product.additionalPoints || [],
          moreProductInfo: product.moreProductInfo || [],
          category:
            typeof product.category === "object"
              ? product.category._id
              : product.category || "",
          isActive: product.isActive ?? true,
        });

        setCategories(categoryRes.data.categories || categoryRes.data);
        toast.success("Product loaded successfully");
      } catch (error) {
        console.error(error);
        toast.error("Failed to load product");
        navigate("/product");
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMainImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        setForm({ ...form, mainImage: url });
        return url;
      }),
      {
        loading: "Uploading main image...",
        success: "Main image updated!",
        error: "Failed to upload image",
      }
    );
  };

  const handleSmallImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    toast.promise(
      Promise.all(files.map(uploadImage)).then((urls) => {
        setForm({ ...form, smallImages: [...form.smallImages, ...urls] });
        return urls;
      }),
      {
        loading: "Uploading gallery images...",
        success: `${files.length} images added to gallery!`,
        error: "Failed to upload images",
      }
    );
  };

  const removeSmallImage = (index) => {
    const newImages = [...form.smallImages];
    newImages.splice(index, 1);
    setForm({ ...form, smallImages: newImages });
    toast.success("Image removed from gallery");
  };

  /* ---------------- BULLET DESCRIPTION ---------------- */
  const addBullet = () =>
    setForm({ ...form, bulletDescription: [...form.bulletDescription, ""] });

  const updateBullet = (i, value) => {
    const bullets = [...form.bulletDescription];
    bullets[i] = value;
    setForm({ ...form, bulletDescription: bullets });
  };

  const removeBullet = (i) => {
    const bullets = [...form.bulletDescription];
    bullets.splice(i, 1);
    setForm({ ...form, bulletDescription: bullets });
    toast.success("Feature removed");
  };

  /* ---------------- ADDITIONAL POINTS ---------------- */
  const addAdditionalPoint = () => {
    setForm({
      ...form,
      additionalPoints: [
        ...form.additionalPoints,
        { title: "", description: "", icon: "" },
      ],
    });
  };

  const updateAdditionalPoint = (i, field, value) => {
    const points = [...form.additionalPoints];
    points[i][field] = value;
    setForm({ ...form, additionalPoints: points });
  };

  const uploadAdditionalIcon = async (i, file) => {
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        updateAdditionalPoint(i, "icon", url);
        return url;
      }),
      {
        loading: "Uploading icon...",
        success: "Icon updated!",
        error: "Failed to upload icon",
      }
    );
  };

  const removeAdditionalPoint = (i) => {
    const points = [...form.additionalPoints];
    points.splice(i, 1);
    setForm({ ...form, additionalPoints: points });
    toast.success("Additional point removed");
  };

  /* ---------------- MORE PRODUCT INFO ---------------- */
  const addMoreInfo = () => {
    setForm({
      ...form,
      moreProductInfo: [
        ...form.moreProductInfo,
        { image: "", title: "", description: "" },
      ],
    });
  };

  const updateMoreInfo = (i, field, value) => {
    const info = [...form.moreProductInfo];
    info[i][field] = value;
    setForm({ ...form, moreProductInfo: info });
  };

  const uploadMoreInfoImage = async (i, file) => {
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        updateMoreInfo(i, "image", url);
        return url;
      }),
      {
        loading: "Uploading image...",
        success: "Image updated!",
        error: "Failed to upload image",
      }
    );
  };

  const removeMoreInfo = (i) => {
    const info = [...form.moreProductInfo];
    info.splice(i, 1);
    setForm({ ...form, moreProductInfo: info });
    toast.success("Info section removed");
  };

  /* ---------------- SUBMIT ---------------- */
  const updateProduct = async () => {
    if (
      !form.heading ||
      !form.description ||
      !form.mainImage ||
      !form.category
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      await axios.put(`${API}/product/edit/${id}`, form);
      toast.success("Product updated successfully!");
      setTimeout(() => navigate("/product"), 1500);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- PREVIEW MODAL ---------------- */
  const PreviewModal = () => {
    if (!showPreview) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Product Preview
            </h2>
            <button
              onClick={() => setShowPreview(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Images */}
              <div>
                {form.mainImage && (
                  <div className="mb-6">
                    <img
                      src={form.mainImage}
                      alt="Main"
                      className="w-full h-96 object-cover rounded-2xl"
                    />
                  </div>
                )}

                {form.smallImages.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Gallery Images
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {form.smallImages.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Details */}
              <div>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {form.heading}
                    </h1>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                        form.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {form.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {form.description && (
                    <p className="text-gray-600 mb-4">{form.description}</p>
                  )}

                  {form.category && (
                    <div className="flex items-center gap-2 mb-4">
                      <Package size={16} className="text-gray-400" />
                      <span className="text-gray-700">
                        Category:{" "}
                        {categories.find((c) => c._id === form.category)
                          ?.name || "Uncategorized"}
                      </span>
                    </div>
                  )}
                </div>

                {form.bulletDescription.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Key Features</h3>
                    <ul className="space-y-2">
                      {form.bulletDescription.map((bullet, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {form.additionalPoints.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">
                      Additional Information
                    </h3>
                    <div className="space-y-4">
                      {form.additionalPoints.map((point, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-xl p-4"
                        >
                          <div className="flex items-start gap-3">
                            {point.icon && (
                              <img
                                src={point.icon}
                                alt=""
                                className="w-10 h-10 rounded-lg"
                              />
                            )}
                            <div>
                              {point.title && (
                                <h4 className="font-semibold text-gray-900">
                                  {point.title}
                                </h4>
                              )}
                              {point.description && (
                                <p className="text-gray-600 mt-1">
                                  {point.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading product data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => navigate("/product")}
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                  Back to Products
                </button>
                <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  Editing: {form.heading || "Product"}
                </span>
              </div>

              <h1 className="text-3xl font-bold text-gray-900">Edit Product</h1>
              <p className="text-gray-600 mt-2">
                Update the details of your product
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-indigo-300 text-indigo-600 rounded-xl font-medium hover:bg-indigo-50 transition-colors"
              >
                <Eye size={18} />
                Preview
              </button>

              <label className="inline-flex items-center cursor-pointer bg-white p-1 rounded-xl border border-gray-300">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                  <div
                    className={`block w-14 h-7 rounded-full transition-colors ${
                      form.isActive ? "bg-green-500" : "bg-gray-300"
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform ${
                      form.isActive ? "transform translate-x-7" : ""
                    }`}
                  ></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {form.isActive ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle size={16} />
                      Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle size={16} />
                      Inactive
                    </span>
                  )}
                </span>
              </label>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: "basic", label: "Basic Info", icon: <Type size={18} /> },
              { id: "images", label: "Media", icon: <Image size={18} /> },
              { id: "content", label: "Content", icon: <FileText size={18} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Type size={22} />
                  Basic Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <input
                      name="heading"
                      placeholder="Enter product name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      value={form.heading}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug
                    </label>
                    <input
                      name="slug"
                      placeholder="product-slug-name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      value={form.slug}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <div className="bg-white rounded-xl border border-gray-200">
                      <ReactQuill
                        theme="snow"
                        value={form.description}
                        onChange={(value) =>
                          setForm({ ...form, description: value })
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Describe your product in detail..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      value={form.category}
                      onChange={handleChange}
                    >
                      <option value="">Select a category</option>
                      {categories.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Images Tab */}
            {activeTab === "images" && (
              <div className="space-y-6">
                {/* Main Image */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Image size={22} />
                    Main Product Image
                  </h2>

                  <div className="text-center">
                    {form.mainImage ? (
                      <div className="relative inline-block">
                        <img
                          src={form.mainImage}
                          alt="Main product"
                          className="w-full max-w-md h-64 object-cover rounded-xl"
                        />
                        <button
                          onClick={() => setForm({ ...form, mainImage: "" })}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                          title="Remove image"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-lg font-medium text-gray-700">
                            Upload main image
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            PNG, JPG or WebP (Max 5MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleMainImage}
                          accept="image/*"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Gallery Images */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    Gallery Images
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    {form.smallImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeSmallImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          title="Remove image"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}

                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                        <Plus className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">
                          Add Images
                        </span>
                      </div>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleSmallImages}
                        accept="image/*"
                      />
                    </label>
                  </div>

                  {form.smallImages.length > 0 && (
                    <p className="text-sm text-gray-500 text-center">
                      {form.smallImages.length} image
                      {form.smallImages.length !== 1 ? "s" : ""} in gallery
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Content Tab */}
            {activeTab === "content" && (
              <div className="space-y-6">
                {/* Bullet Points */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <List size={22} />
                    Key Features
                  </h2>

                  {form.bulletDescription.map((bullet, i) => (
                    <div key={i} className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <div className="relative">
                          <input
                            placeholder="Enter feature description"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                            value={bullet}
                            onChange={(e) => updateBullet(i, e.target.value)}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeBullet(i)}
                        className="p-3 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                        title="Remove feature"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={addBullet}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add Feature
                  </button>
                </div>

                {/* Additional Points */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Info size={22} />
                    Additional Information
                  </h2>

                  {form.additionalPoints.map((point, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 rounded-xl p-4 mb-4 bg-gray-50/50"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-indigo-600">
                              {i + 1}
                            </span>
                          </div>
                          Additional Point
                        </h3>
                        <button
                          onClick={() => removeAdditionalPoint(i)}
                          className="text-red-500 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg"
                          title="Remove point"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <input
                          placeholder="Title"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                          value={point.title}
                          onChange={(e) =>
                            updateAdditionalPoint(i, "title", e.target.value)
                          }
                        />

                        <textarea
                          placeholder="Description"
                          rows="2"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
                          value={point.description}
                          onChange={(e) =>
                            updateAdditionalPoint(
                              i,
                              "description",
                              e.target.value
                            )
                          }
                        />

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">
                            Icon
                          </label>
                          {point.icon ? (
                            <div className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                              <img
                                src={point.icon}
                                alt="Icon"
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">
                                  Icon uploaded
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <label className="cursor-pointer">
                                  <div className="px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                                    Change
                                  </div>
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) =>
                                      uploadAdditionalIcon(i, e.target.files[0])
                                    }
                                    accept="image/*"
                                  />
                                </label>
                                <button
                                  onClick={() =>
                                    updateAdditionalPoint(i, "icon", "")
                                  }
                                  className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer block">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <span className="text-sm text-gray-600">
                                  Upload Icon
                                </span>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) =>
                                  uploadAdditionalIcon(i, e.target.files[0])
                                }
                                accept="image/*"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addAdditionalPoint}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add Additional Point
                  </button>
                </div>

                {/* More Product Info */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Grid size={22} />
                    Detailed Information
                  </h2>

                  {form.moreProductInfo.map((info, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 rounded-xl p-4 mb-4 bg-gray-50/50"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-purple-600">
                              {i + 1}
                            </span>
                          </div>
                          Info Section
                        </h3>
                        <button
                          onClick={() => removeMoreInfo(i)}
                          className="text-red-500 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg"
                          title="Remove section"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <input
                          placeholder="Title"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                          value={info.title}
                          onChange={(e) =>
                            updateMoreInfo(i, "title", e.target.value)
                          }
                        />

                        <div className="bg-white rounded-xl border border-gray-200">
                          <ReactQuill
                            theme="snow"
                            value={info.description}
                            onChange={(value) =>
                              updateMoreInfo(i, "description", value)
                            }
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your product in detail..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">
                            Image
                          </label>
                          {info.image ? (
                            <div className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                              <img
                                src={info.image}
                                alt="Section"
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">
                                  Image uploaded
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <label className="cursor-pointer">
                                  <div className="px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                                    Change
                                  </div>
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) =>
                                      uploadMoreInfoImage(i, e.target.files[0])
                                    }
                                    accept="image/*"
                                  />
                                </label>
                                <button
                                  onClick={() => updateMoreInfo(i, "image", "")}
                                  className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer block">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <span className="text-sm text-gray-600">
                                  Upload Image
                                </span>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) =>
                                  uploadMoreInfoImage(i, e.target.files[0])
                                }
                                accept="image/*"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addMoreInfo}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add More Information
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Actions & Info */}
          <div className="space-y-6">
            {/* Product Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 ">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Product Summary
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Product ID
                  </h3>
                  <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded-lg truncate">
                    {id}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Status
                    </h3>
                    <p
                      className={`inline-flex items-center gap-1 font-semibold ${
                        form.isActive ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {form.isActive ? (
                        <CheckCircle size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      {form.isActive ? "Active" : "Inactive"}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Features
                    </h3>
                    <p className="font-semibold text-gray-900">
                      {form.bulletDescription.length}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Category
                  </h3>
                  <p className="font-semibold text-gray-900">
                    {categories.find((c) => c._id === form.category)?.name ||
                      "Not selected"}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Gallery Images:</span>
                    <span className="font-semibold">
                      {form.smallImages.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Additional Points:</span>
                    <span className="font-semibold">
                      {form.additionalPoints.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Info Sections:</span>
                    <span className="font-semibold">
                      {form.moreProductInfo.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Actions</h2>

              <div className="space-y-3">
                <button
                  onClick={updateProduct}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Update Product
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate("/product")}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Reset Changes
                </button>
              </div>
            </div>

            {/* Required Fields Warning */}
            {(!form.heading ||
              !form.description ||
              !form.mainImage ||
              !form.category) && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-500 mt-0.5" size={20} />
                  <div>
                    <h3 className="font-semibold text-red-700">
                      Required Fields Missing
                    </h3>
                    <ul className="mt-2 space-y-1 text-sm text-red-600">
                      {!form.heading && <li>• Product name is required</li>}
                      {!form.description && <li>• Description is required</li>}
                      {!form.mainImage && <li>• Main image is required</li>}
                      {!form.category && <li>• Category is required</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <PreviewModal />
    </div>
  );
};

export default ProductEdit;

// import axios from "axios";
// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import toast from "react-hot-toast";
// import { uploadImage } from "../../common/uploadImage";

// const ProductEdit = () => {
//   const { id } = useParams();
//   const API = process.env.REACT_APP_API_URL;

//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [form, setForm] = useState({
//     heading: "",
//     slug: "",
//     description: "",
//     mainImage: "",
//     smallImages: [],
//     bulletDescription: [],
//     additionalPoints: [],
//     moreProductInfo: [],
//     category: "",
//     isActive: true,
//   });

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const [productRes, categoryRes] = await Promise.all([
//           axios.get(`${API}/product/${id}`),
//           axios.get(`${API}/categories`),
//         ]);

//         const product = productRes.data.product; // 👈 matches your API
//         console.log("first", product);
//         setForm({
//           heading: product.heading || "",
//           slug: product.slug || "",
//           description: product.description || "",
//           mainImage: product.mainImage || "",
//           smallImages: product.smallImages || [],
//           bulletDescription: product.bulletDescription || [],
//           additionalPoints: product.additionalPoints || [],
//           moreProductInfo: product.moreProductInfo || [],
//           category: product.category || "", // 👈 string ID (correct)
//           isActive: product.isActive ?? true,
//         });

//         setCategories(categoryRes.data.categories || categoryRes.data);
//       } catch (error) {
//         console.error(error);
//         toast.error("Failed to load product");
//       }
//     };

//     fetchData();
//   }, [id]);

//   /* ---------------- HANDLERS ---------------- */
//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   const handleMainImage = async (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     const url = await uploadImage(file);
//     setForm({ ...form, mainImage: url });
//   };

//   const handleSmallImages = async (e) => {
//     const files = Array.from(e.target.files || []);
//     if (!files.length) return;

//     const urls = await Promise.all(files.map(uploadImage));
//     setForm({ ...form, smallImages: [...form.smallImages, ...urls] });
//   };

//   /* ---------------- BULLET DESCRIPTION ---------------- */
//   const addBullet = () =>
//     setForm({ ...form, bulletDescription: [...form.bulletDescription, ""] });

//   const updateBullet = (i, value) => {
//     const bullets = [...form.bulletDescription];
//     bullets[i] = value;
//     setForm({ ...form, bulletDescription: bullets });
//   };

//   /* ---------------- ADDITIONAL POINTS ---------------- */
//   const addAdditionalPoint = () => {
//     setForm({
//       ...form,
//       additionalPoints: [
//         ...form.additionalPoints,
//         { title: "", description: "", icon: "" },
//       ],
//     });
//   };

//   const updateAdditionalPoint = (i, field, value) => {
//     const points = [...form.additionalPoints];
//     points[i][field] = value;
//     setForm({ ...form, additionalPoints: points });
//   };

//   const uploadAdditionalIcon = async (i, file) => {
//     if (!file) return;
//     const url = await uploadImage(file);
//     updateAdditionalPoint(i, "icon", url);
//   };

//   /* ---------------- MORE PRODUCT INFO ---------------- */
//   const addMoreInfo = () => {
//     setForm({
//       ...form,
//       moreProductInfo: [
//         ...form.moreProductInfo,
//         { image: "", title: "", description: "" },
//       ],
//     });
//   };

//   const updateMoreInfo = (i, field, value) => {
//     const info = [...form.moreProductInfo];
//     info[i][field] = value;
//     setForm({ ...form, moreProductInfo: info });
//   };

//   const uploadMoreInfoImage = async (i, file) => {
//     if (!file) return;
//     const url = await uploadImage(file);
//     updateMoreInfo(i, "image", url);
//   };

//   /* ---------------- SUBMIT ---------------- */
//   const updateProduct = async () => {
//     if (
//       !form.heading ||
//       !form.description ||
//       !form.mainImage ||
//       !form.category
//     ) {
//       toast.error("Please fill all required fields");
//       return;
//     }

//     setLoading(true);

//     try {
//       await axios.put(`${API}/product/${id}`, form);
//       toast.success("Product updated successfully");
//       window.history.back();
//     } catch (error) {
//       console.error(error);
//       toast.error("Failed to update product");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="p-6 max-w-5xl mx-auto space-y-6">
//       <h1 className="text-2xl font-bold">Edit Product</h1>

//       <input
//         name="heading"
//         placeholder="Heading"
//         className="input"
//         value={form.heading}
//         onChange={handleChange}
//       />

//       <input
//         name="slug"
//         placeholder="Slug"
//         className="input"
//         value={form.slug}
//         onChange={handleChange}
//       />

//       <textarea
//         name="description"
//         placeholder="Description"
//         className="input"
//         value={form.description}
//         onChange={handleChange}
//       />

//       {/* CATEGORY */}
//       <select
//         name="category"
//         className="input"
//         value={form.category}
//         onChange={handleChange}
//       >
//         <option value="">Select Category</option>
//         {categories.map((c) => (
//           <option key={c._id} value={c._id}>
//             {c.name}
//           </option>
//         ))}
//       </select>

//       {/* MAIN IMAGE */}
//       <div>
//         <label>Main Image</label>
//         <input type="file" onChange={handleMainImage} />
//         {form.mainImage && <img src={form.mainImage} className="h-24 mt-2" />}
//       </div>

//       {/* SMALL IMAGES */}
//       <div>
//         <label>Small Images</label>
//         <input type="file" multiple onChange={handleSmallImages} />
//       </div>

//       {/* BULLET DESCRIPTION */}
//       <div>
//         <h3 className="font-semibold">Bullet Description</h3>
//         {form.bulletDescription.map((b, i) => (
//           <input
//             key={i}
//             className="input mt-2"
//             value={b}
//             onChange={(e) => updateBullet(i, e.target.value)}
//           />
//         ))}
//         <button onClick={addBullet} className="btn mt-2">
//           + Add Bullet
//         </button>
//       </div>

//       {/* ADDITIONAL POINTS */}
//       <div>
//         <h3 className="font-semibold">Additional Points</h3>
//         {form.additionalPoints.map((p, i) => (
//           <div key={i} className="border p-3 mt-2 space-y-2">
//             <input
//               placeholder="Title"
//               className="input"
//               value={p.title}
//               onChange={(e) =>
//                 updateAdditionalPoint(i, "title", e.target.value)
//               }
//             />
//             <textarea
//               placeholder="Description"
//               className="input"
//               value={p.description}
//               onChange={(e) =>
//                 updateAdditionalPoint(i, "description", e.target.value)
//               }
//             />
//             <input
//               type="file"
//               onChange={(e) => uploadAdditionalIcon(i, e.target.files[0])}
//             />
//           </div>
//         ))}
//         <button onClick={addAdditionalPoint} className="btn mt-2">
//           + Add Additional Point
//         </button>
//       </div>

//       {/* MORE PRODUCT INFO */}
//       <div>
//         <h3 className="font-semibold">More Product Info</h3>
//         {form.moreProductInfo.map((m, i) => (
//           <div key={i} className="border p-3 mt-2 space-y-2">
//             <input
//               placeholder="Title"
//               className="input"
//               value={m.title}
//               onChange={(e) => updateMoreInfo(i, "title", e.target.value)}
//             />
//             <textarea
//               placeholder="Description"
//               className="input"
//               value={m.description}
//               onChange={(e) => updateMoreInfo(i, "description", e.target.value)}
//             />
//             <input
//               type="file"
//               onChange={(e) => uploadMoreInfoImage(i, e.target.files[0])}
//             />
//           </div>
//         ))}
//         <button onClick={addMoreInfo} className="btn mt-2">
//           + Add More Info
//         </button>
//       </div>

//       <button
//         onClick={updateProduct}
//         disabled={loading}
//         className={`px-6 py-2 rounded text-white ${
//           loading ? "bg-gray-400" : "bg-blue-600"
//         }`}
//       >
//         {loading ? "Updating..." : "Update Product"}
//       </button>
//     </div>
//   );
// };

// export default ProductEdit;
